import {
  getPassportApiHost,
  getOpenapiPassportApiHost,
  getEnv,
  isLocal,
  setLoginUinfo,
  getLoginUinfo,
  toLogin,
} from '@m2h/common/utils';
import {
  gerIdcByRegion,
  REGION_IDC_MAP,
  SUPPORT_REGIONS,
} from '@sailor/compass';
import {
  setLoginAccountId,
  getLoginAccountId,
} from '../utils/common';
import { pureRequest } from '../request/index.h5';
import {
  passportConfig,
} from '../constants';
import {
  setGlobalState, getGlobalState,
} from '../utils/globalState';

// 无需登录的页面
const noLoginPages = [];

// 需登录的页面
const needLoginPages = [
  '/mpweb/order_list',
  '/mpweb/order_confirm_golden',
  '/mpweb/mine_address',
  '/mpweb/shop_favorites',
];

const USERAPI = {
  getUinfo: '/api/user/v1/info',
  switchAuth: '/api/user/v1/switchAuth',
};

export interface PassportSwitchAuthParams {
  sourceRegion: string;
  switchRegion: string;
}

export interface PassportSwitchAuthData {
  status: number;
  id?: string | number;
  idStr?: string;
}

export interface PassportSwitchAuthError {
  code?: string | number;
  type?: string;
  message: string;
}

export interface PassportSwitchAuthResult {
  success: boolean;
  data?: PassportSwitchAuthData;
  error?: PassportSwitchAuthError;
}

const createSwitchAuthError = (
  message: string,
  code: string | number,
  type = 'PASSPORT_SWITCH_AUTH_ERROR',
): PassportSwitchAuthResult => ({
  success: false,
  error: { code, type, message },
});

const normalizeSwitchAuthError = (
  error: any,
  fallbackMessage: string,
  fallbackCode: string,
): PassportSwitchAuthError => {
  const responseError = error?.response?.data?.error || error?.response?.data;
  const payload = responseError || error?.error || error || {};
  return {
    code: payload.code ?? error?.code ?? fallbackCode,
    type: payload.type ?? error?.type ?? 'PASSPORT_SWITCH_AUTH_ERROR',
    message: payload.message || error?.message || fallbackMessage,
  };
};

const SUPPORT_REGION_SET = new Set(SUPPORT_REGIONS);
const normalizeRegion = (region: unknown): string => {
  if (typeof region !== 'string') return '';
  const normalizedRegion = region.trim().toUpperCase();
  return SUPPORT_REGION_SET.has(normalizedRegion) ? normalizedRegion : '';
};

const getKnownRegionIDC = (region: string): string => {
  if (!region || !Object.prototype.hasOwnProperty.call(REGION_IDC_MAP, region)) return '';
  return gerIdcByRegion(region as any);
};

/**
 * 使用旧 Token 对应的 sourceRegion 选择 Passport IDC，并授权目标 Region。
 * Token 是 HttpOnly Cookie，只由浏览器随请求携带并接收服务端 Set-Cookie。
 */
export async function switchPassportAuth({
  sourceRegion,
  switchRegion,
}: PassportSwitchAuthParams): Promise<PassportSwitchAuthResult> {
  const normalizedSourceRegion = normalizeRegion(sourceRegion);
  const normalizedSwitchRegion = normalizeRegion(switchRegion);
  const sourceIDC = getKnownRegionIDC(normalizedSourceRegion);
  const switchIDC = getKnownRegionIDC(normalizedSwitchRegion);

  if (!sourceIDC) {
    return createSwitchAuthError(
      'Invalid source Region for Passport authorization',
      'INVALID_SOURCE_REGION',
    );
  }
  if (!switchIDC) {
    return createSwitchAuthError(
      'Invalid target Region for Passport authorization',
      'INVALID_SWITCH_REGION',
    );
  }

  const env = getEnv();
  const passportConf = passportConfig[env];
  const passportHost = getOpenapiPassportApiHost(normalizedSourceRegion);
  const tokenId = passportConf?.tokenId;

  if (!passportHost) {
    return createSwitchAuthError(
      'Passport switchAuth host is unavailable',
      'PASSPORT_SWITCH_AUTH_HOST_UNAVAILABLE',
    );
  }
  if (!tokenId) {
    return createSwitchAuthError(
      'Passport consumer configuration is unavailable',
      'PASSPORT_SWITCH_AUTH_CONFIG_UNAVAILABLE',
    );
  }

  const params = {
    token_id: tokenId,
    switch_region: normalizedSwitchRegion,
    set_cookie: true,
  };

  try {
    const response: any = await pureRequest({
      url: `https://${passportHost}${USERAPI.switchAuth}`,
      method: 'POST',
      params,
      withCredentials: true,
    });

    const responseData = response?.data;
    if (responseData?.status === 1) {
      const { status, id, idStr } = responseData;
      return {
        success: true,
        data: { status, id, idStr },
      };
    }

    return {
      success: false,
      error: normalizeSwitchAuthError(
        response?.error || responseData,
        'Passport Region authorization was not granted',
        'PASSPORT_SWITCH_AUTH_NOT_GRANTED',
      ),
    };
  } catch (error) {
    return {
      success: false,
      error: normalizeSwitchAuthError(
        error,
        'Passport Region authorization request failed',
        'PASSPORT_SWITCH_AUTH_REQUEST_FAILED',
      ),
    };
  }
}

// 刷新页面时标记为初次请求接口
setGlobalState('isGettingUserInfo', true);

export async function fetchPassportUserInfo() {
  try {
    setGlobalState('isGettingUserInfo', true);
    const res = await pureRequest({
      url: `https://${getPassportApiHost()}${USERAPI.getUinfo}`,
      method: 'GET',
      params: {
        fields: 'id,username,email,mobile,hasPassword,avatartype,avatarurl,tokenChannel',
      },
      withCredentials: true, // 携带cookie
    });
    const { user, error } = res || {};
    // 当前路径，区分是否要跳登录
    const urlPath = window.location.pathname;
    const _isLocal = isLocal(window.location.host);
    if (!_isLocal && error && +error.code === 401 && needLoginPages.includes(urlPath)) {
      toLogin();
      return false;
    }
    const {
      username, idStr, email, countryCode, tokenChannel
    } = user || {};
    const uInfo = {
      username: username || email || '',
      userName: username || email || '',
      email,
      id: idStr || 0,
      uid: idStr || 0,
      userId: idStr || 0,
      countryCode,
      tokenChannel,
    };
    setLoginUinfo(uInfo);
    setLoginAccountId(uInfo.uid);
    setGlobalState('isGettingUserInfo', false);

    if (window.LXAnalytics) {
      // @ts-ignore
      const tracker = window.LXAnalytics('getTracker');
      // @ts-ignore
      tracker && (idStr && idStr !== '0') && tracker('set', 'uid', idStr);
    }

    // setCookie('accountId', uInfo.uid);
    Mach.sendEvent('getUserInfo');
    // @ts-ignore
    // Mach.off('getUserInfo');
    return uInfo;
  } catch (e) {
    console.log('updatePassportUserInfoError', e);
    setGlobalState('isGettingUserInfo', false);
    Mach.sendEvent('getUserInfo');
    // @ts-ignore
    // Mach.off('getUserInfo');
    // const uInfo = DEFAULT_PASSPORT_USERINFO;
    // setPassportUserInfo(uInfo);
    return {};
  }
}

// 单页应用切路由不会执行到
export async function getPassportUserInfo() {
  try {
    const isGettingUserInfo = getGlobalState('isGettingUserInfo');

    // 检查是否已有有效的用户ID，如果有则直接使用存储的用户信息
    const existingUserId = getLoginAccountId() || {};
    const existingUserInfo = getLoginUinfo();

    try {
      // 如果已有有效的用户ID和用户信息，直接返回存储的数据
      // 需要考虑登录已失效，但是存储数据未清空的情况。所以还要继续请求接口
      if (
        existingUserId
        && existingUserId !== '0'
        && existingUserId !== ''
        && existingUserInfo
        && existingUserInfo.uid
        && existingUserInfo.uid !== '0'
        && +existingUserId > 0
        && +existingUserId > 0
        && +existingUserInfo.uid > 0
        && !isGettingUserInfo
      ) {
        console.log('使用存储的用户信息，跳过网络请求', existingUserInfo);

        // 设置灵犀用户ID
        if (window.LXAnalytics) {
          // @ts-ignore
          const tracker = window.LXAnalytics('getTracker');
          // @ts-ignore
          tracker && tracker('set', 'uid', existingUserId);
        }

        // 触发事件通知
        Mach.sendEvent('getUserInfo');
        // @ts-ignore
        Mach.off('getUserInfo');

        return existingUserInfo;
      }
    } catch (e) {
      console.log(e, 'getPassportUserInfo existing info err');
    }

    return await fetchPassportUserInfo();
  } catch (e) {
    console.log('updatePassportUserInfoError', e);
    setGlobalState('isGettingUserInfo', false);
    Mach.sendEvent('getUserInfo');
    // @ts-ignore
    // Mach.off('getUserInfo');
    // const uInfo = DEFAULT_PASSPORT_USERINFO;
    // setPassportUserInfo(uInfo);
    return {};
  }
}

// export function getPassportUserInfoReq() {
//   console.log('getUserInfo|params');
//   const url = `//${getPassportApiHost()}${USERAPI.getUinfo}`;
//   const params = {
//     fields: 'id,username,email,mobile,hasPassword,avatartype,avatarurl',
//   };
//   return (request({
//     url,
//     method: 'POST',
//     data: params,
//   })) as Promise<any>;
// }
